import { type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { usePersistedState } from '../../lib/usePersistedState';

// `collapsible` is opt-in (default: today's always-open behavior, unchanged
// for every other caller) - pass an `id` unique among sections sharing a
// title elsewhere in the app (e.g. "integration-overview") so the persisted
// open/closed state doesn't collide across screens. Falls back to a
// slugified `title` when `id` is omitted, which is fine as long as nothing
// else collapsible on the same page reuses that same title.
export function FormSection({
  title,
  children,
  collapsible = false,
  id,
  defaultOpen = true,
}: {
  title: string;
  children: ReactNode;
  collapsible?: boolean;
  id?: string;
  defaultOpen?: boolean;
}) {
  const storageKey = `formSection.${id ?? title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.open`;
  const [open, setOpen] = usePersistedState<boolean>(storageKey, defaultOpen, (v) => typeof v === 'boolean');
  const isOpen = !collapsible || open;

  return (
    <div className="flex flex-col gap-3">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={isOpen}
          className="flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70"
        >
          <ChevronDown size={13} className={`transition-transform ${isOpen ? '' : '-rotate-90'}`} />
          {title}
        </button>
      ) : (
        <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 px-1">{title}</span>
      )}
      {isOpen && (
        <div className="rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl p-4 sm:p-5 flex flex-col gap-4">
          {children}
        </div>
      )}
    </div>
  );
}
